"use client";

import type {
  ISchedule,
  ScheduleBuffer,
  WorkplaceRequestReservationTicketWorkflow
} from "@gettactic/api";
import {
  EMPTY_BUFFER,
  ReservationBufferFields,
  ReservationBufferSummary
} from "@gettactic/components/src/components/reservation-buffer/ReservationBufferFields";
import {
  useAuthenticated,
  useOfficesQuery,
  useResourceEventsForDays,
  useResourcesByIds
} from "@gettactic/hooks";
import { format } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { AlertTriangle } from "lucide-react";
import React from "react";
import { classes } from "@/features/settings/components/WorkplaceRequestsPage/components/RequestDetailsModal/utils";
import { RoomDayViewPopover } from "./RoomDayViewPopover";
import {
  findRoomBufferConflict,
  type RoomBufferConflict
} from "./useRoomBufferConflict";

interface RequestBufferSectionProps {
  workflow: WorkplaceRequestReservationTicketWorkflow;
  /** Shown when more than one workflow books a room for this request. */
  showWorkflowName: boolean;
  /** The request's office, the next best guess until the room's own is known. */
  officeId?: string;
  value: ScheduleBuffer;
  onChange: (value: ScheduleBuffer) => void;
}

/** These Dates already carry the office wall clock in their local fields. */
const formatClock = (date: Date) => format(date, "h:mmaaa");

/**
 * `useResourceEventsForDays` groups its result per reservation; the buffer only
 * ever needs one row per reservation, and every row of a group shares its times.
 */
const toSchedules = (groups: (ISchedule | ISchedule[])[] | undefined) =>
  (groups ?? [])
    .map((group) => (Array.isArray(group) ? group[0] : group))
    .filter((schedule): schedule is ISchedule => !!schedule);

/**
 * The buffer the approval would book the room with, editable before approving.
 * It sets nothing on its own: the value travels to the approval as a workflow
 * override, and the "Approve and book" step opens on the same number.
 */
export const RequestBufferSection: React.FC<RequestBufferSectionProps> = ({
  workflow,
  showWorkflowName,
  officeId,
  value,
  onChange
}) => {
  const {
    userContext: { deviceTz, authenticatedUser }
  } = useAuthenticated();
  const officesQuery = useOfficesQuery();

  const resolved = workflow.resolved;
  const resolvedPeriods = resolved?.periods;
  const periods = React.useMemo(() => resolvedPeriods ?? [], [resolvedPeriods]);
  // A multi-day reservation books one period per day, all at the same hours, so
  // the first one stands for the buffer window shown here.
  const period = periods[0];
  const resourceId = resolved?.resource_id ?? null;
  const { resources } = useResourcesByIds(resourceId ? [resourceId] : []);
  const room = resources?.[0];

  // A reservation lives in its room's office, so its clock is that office's —
  // the request's office only stands in until the room has loaded.
  const office = officesQuery.data?.offices?.find(
    (candidate) => candidate.id === (room?.office_id ?? officeId)
  );
  const tz =
    office?.time_zone ?? authenticatedUser?.user?.time_zone ?? deviceTz;

  // Wall clock in the office time zone: the reservation's hours as the room
  // lives them, which is also how the day view and the pickers read them.
  const start = period ? toZonedTime(period.start, tz) : null;
  const end = period ? toZonedTime(period.end, tz) : null;

  // One day per period, so a clash on a later day of a multi-day booking is
  // caught too. Without periods there is no day to ask about, and the endpoint
  // rejects the call outright — so nothing is fetched until it is resolved.
  const days = React.useMemo(
    () => periods.map((p) => format(toZonedTime(p.start, tz), "yyyy-MM-dd")),
    [periods, tz]
  );
  const dayQueries = useResourceEventsForDays({
    days,
    officeId: office?.id ?? null,
    tz,
    resourceId
  });
  const schedules = toSchedules(dayQueries[0]?.data);

  // The first day whose buffer would run into another booking in the room.
  const conflict = periods.reduce<(RoomBufferConflict & { day: Date }) | null>(
    (found, p, index) => {
      if (found) return found;
      const dayStart = toZonedTime(p.start, tz);
      const dayConflict = findRoomBufferConflict({
        start: dayStart,
        end: toZonedTime(p.end, tz),
        buffer: value,
        schedules: toSchedules(dayQueries[index]?.data)
      });
      return dayConflict ? { ...dayConflict, day: dayStart } : null;
    },
    null
  );

  if (!resolved || !start || !end) return null;
  // Rooms only, like the reservation panel and the approval modal: a desk has
  // nothing to set up, and the office buffer already covers the gap desks need.
  if (room?.type !== "meeting_room") return null;

  return (
    <div className="border-neutral-200 border-t pt-5">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className={classes.sectionHeading}>
          {showWorkflowName && workflow.name
            ? `${workflow.name} buffer`
            : "Room buffer"}
        </span>
        {room ? (
          <RoomDayViewPopover
            roomName={room.name || room.label || "Room"}
            schedules={schedules}
            day={start}
            pending={{
              title: resolved.title ?? "",
              start,
              end,
              buffer: value
            }}
            office={office}
          />
        ) : null}
      </div>

      <ReservationBufferFields
        value={value ?? EMPTY_BUFFER}
        onChange={onChange}
      />
      <ReservationBufferSummary
        start={start}
        end={end}
        buffer={value}
        className="mt-2"
      />

      {conflict ? (
        <p className="mt-3 flex items-start gap-2 rounded-lg bg-amber-50 p-2.5 font-inter text-amber-900 text-xs">
          <AlertTriangle
            aria-hidden="true"
            className="mt-px h-4 w-4 shrink-0"
          />
          <span>
            Buffer overlaps &ldquo;{conflict.title}&rdquo; (
            {periods.length > 1 ? `${format(conflict.day, "EEE d MMM")}, ` : ""}
            {formatClock(conflict.start)} — {formatClock(conflict.end)}) in this
            room.
          </span>
        </p>
      ) : null}
    </div>
  );
};
